import { Box, Tooltip, Typography } from "@mui/material"
import { PieChart } from "@mui/x-charts-pro"
import { extraColors } from "../../utils/theme"

interface StatusCounts {
  fmc: number
  pmc: number
  nmc: number
  total: number
}

const STATUS_COLORS = {
  fmc: extraColors.status.greenLight,
  pmc: extraColors.status.yellowLight,
  nmc: extraColors.status.redLight,
} as const

interface NodePieMarkerProps {
  counts: StatusCounts
  /** Size in px. Defaults to 40. */
  size?: number
  /** Number of nodes represented (shown as badge). */
  nodeCount?: number
  /** Names shown in the hover tooltip. */
  nodeNames?: string[]
  onClick?: () => void
  /** Whether this marker is part of the current lasso selection. */
  selected?: boolean
}

/**
 * A small donut chart rendered as a map marker.
 * Slices represent FMC / PMC / NMC sensor counts.
 */
export const NodePieMarker = ({
  counts,
  size = 40,
  nodeCount,
  nodeNames,
  onClick,
  selected,
}: NodePieMarkerProps) => {
  const other = counts.total - counts.fmc - counts.pmc - counts.nmc
  const data: { id: string; value: number; color: string }[] = [
    { id: "fmc", value: counts.fmc, color: STATUS_COLORS.fmc },
    { id: "pmc", value: counts.pmc, color: STATUS_COLORS.pmc },
    { id: "nmc", value: counts.nmc, color: STATUS_COLORS.nmc },
    { id: "other", value: Math.max(0, other), color: extraColors.disabled },
  ].filter(d => d.value > 0)

  // If no sensors at all, show an empty ring
  if (data.length === 0) {
    data.push({ id: "empty", value: 1, color: extraColors.disabled })
  }

  const tooltipTitle =
    nodeNames && nodeNames.length > 0 ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {nodeNames.map(n => (
          <Typography key={n} variant="caption" sx={{ lineHeight: 1.3 }}>
            {n}
          </Typography>
        ))}
      </Box>
    ) : null

  return (
    <Tooltip title={tooltipTitle} arrow placement="top" disableInteractive>
      <Box
        onClick={evt => {
          evt.stopPropagation()
          onClick?.()
        }}
        sx={{
          position: "relative",
          width: size,
          height: size,
          cursor: "pointer",
          transition: "transform 0.15s ease",
          "&:hover": { transform: "scale(1.2)" },
          ...(selected && {
            outline: `2px solid ${extraColors.status.navyLight}`,
            outlineOffset: 3,
            borderRadius: "50%",
          }),
        }}
      >
        <PieChart
          skipAnimation
          series={[
            {
              data,
              innerRadius: size * 0.25,
              outerRadius: size * 0.5,
              paddingAngle: 1,
              cornerRadius: 1,
            },
          ]}
          width={size}
          height={size}
          hideLegend
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          slots={{ tooltip: () => null }}
        />
        {/* Center count label */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: size * 0.5,
            height: size * 0.5,
            borderRadius: "50%",
            bgcolor: extraColors.dark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{
              fontSize: size * 0.28,
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            {counts.total}
          </Typography>
        </Box>
        {/* Node count badge (for clusters) */}
        {nodeCount != null && nodeCount > 1 && (
          <Box
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              bgcolor: extraColors.status.navyLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.4,
            }}
          >
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              {nodeCount}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  )
}
